import { FormattedMsg } from '@components/i18n/formattedMsg';
import Dollar from '@components/shared/number/Dollar';
import { Col, Layout, Row, Tabs, Card } from 'antd';
import React, { useMemo } from 'react';
import { isNumber } from 'util';
import Router from 'next/router';
import memoizeOne from 'memoize-one';
import FunderBreadCrumb from './FunderBreadCrumb';

export interface IFunderLayoutHeader {
    funderName?: string;
    path?: string;
    freeCashHKD?: number;
    freeCashUSD?: number;
}

const { TabPane } = Tabs;

const TabOptions = Object.freeze([
    {
        name: 'marketplace-tab-pane',
        label: <FormattedMsg id='tab.title.marketplace' />,
        routeReg: new RegExp('/marketplace'),
        url: '/marketplace',
    },
    {
        name: 'balance-asset-tab-pane',
        label: <FormattedMsg id='tab.title.balance-asset' />,
        routeReg: new RegExp('/balance-asset'),
        url: '/balance-asset/account-summary',
    },
    {
        name: 'profile-setting-tab-pane',
        label: <FormattedMsg id='tab.title.profile-setting' />,
        routeReg: new RegExp('/profile'),
        url: '/profile',
    },
]);

function RoutingHandler(activeKey: string): void {
    const tab = TabOptions.find((elem) => elem.name === activeKey);
    Router.push(tab.url);
}

const RenderTab = memoizeOne((path: string) => {
    const tabList = TabOptions.map((elem) => (
        <TabPane data-test-id={elem.name} tab={elem.label} key={elem.name} />
    ));
    const currentTab = TabOptions.find((elem) => elem.routeReg.test(path));
    return (
        <Tabs
            activeKey={currentTab?.name}
            onTabClick={RoutingHandler}
            tabBarStyle={{ marginBottom: '0' }}
        >
            {tabList}
        </Tabs>
    );
});

interface INameBlockProps {
    funderName?: string;
}
const NameBlock: React.FunctionComponent<INameBlockProps> = ({
    funderName,
}) => (
    <>
        <Row>
            <FunderBreadCrumb />
        </Row>
        <br />
        <Row>
            <b style={{ fontSize: '20px', color: 'black' }}>
                <FormattedMsg id='common.welcome' />, {funderName}
            </b>
        </Row>
    </>
);

interface IFreeCashBlock {
    currency: 'HKD' | 'USD';
    balance: number;
}
const FreeCashBlock: React.FunctionComponent<IFreeCashBlock> = ({
    currency,
    balance,
}) => useMemo(()=>(
    <>
        <Row align='middle' justify='end'>
            <FormattedMsg id='common.free-cash-balance-title' /> ({currency})
        </Row>

        <Row align='middle' justify='end'>
            <h3>
                <b>
                    {isNumber(balance) ? (
                        <Dollar currency='' parenthesesIndicator={balance}>
                            {balance}
                        </Dollar>
                    ) : (
                        'NA'
                    )}
                </b>
            </h3>
        </Row>
    </>
), [currency, balance]);

const FunderLayoutHeader = (props: IFunderLayoutHeader) => {
    return (
        <Layout
            style={{
                background: '#fff',
                paddingLeft: '32px',
                paddingRight: '32px',
                paddingTop: '16px',
            }}
        >
            <Layout.Content>
                <Row gutter={24}>
                    <Col span={14}>
                        <NameBlock funderName={props.funderName} />
                    </Col>

                    <Col span={10}>
                        <Card
                            bordered={false}
                            style={{
                                maxWidth: '700px',
                                minWidth: '420px',
                                width: '100%',
                                float: 'right',
                            }}
                        >
                            <Card.Grid
                                hoverable={false}
                                style={{
                                    width: '50%',
                                    boxShadow: 'none',
                                    borderRightStyle: 'solid',
                                    borderRightColor: '#d9d9d9',
                                    borderRightWidth: '0.2px',
                                }}
                            >
                                <FreeCashBlock
                                    currency='USD'
                                    balance={props.freeCashUSD}
                                />
                            </Card.Grid>

                            <Card.Grid
                                hoverable={false}
                                style={{ width: '50%', boxShadow: 'none' }}
                            >
                                <FreeCashBlock
                                    currency='HKD'
                                    balance={props.freeCashHKD}
                                />
                            </Card.Grid>
                        </Card>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>{RenderTab(props.path)}</Col>
                </Row>
            </Layout.Content>
        </Layout>
    );
};

export default FunderLayoutHeader;
